import React from 'react';
import { X, Trash2, Minus, Plus, ShoppingBag, ImageOff, ArrowLeft } from 'lucide-react';
import { CartContextType } from '../types';

interface CartSidebarProps {
  cart: CartContextType;
  onCheckout: () => void;
}

export const CartSidebar: React.FC<CartSidebarProps> = ({ cart, onCheckout }) => {
  if (!cart.isCartOpen) return null;

  // Safe items check
  const items = Array.isArray(cart.items) ? cart.items : [];

  return (
    <div className="fixed inset-0 z-[100] overflow-hidden" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity backdrop-blur-sm"
        onClick={() => cart.setIsCartOpen(false)}
      ></div>

      <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
        <div className="w-screen max-w-md transform transition-transform bg-white shadow-xl flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-6 sm:px-6 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <ShoppingBag size={20} />
              Shopping Cart
            </h2>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
              onClick={() => cart.setIsCartOpen(false)}
            >
              <span className="sr-only">Close panel</span>
              <X size={24} />
            </button>
          </div>

          {/* Cart Items */}
          <div className="flex-1 py-6 overflow-y-auto px-4 sm:px-6">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center space-y-4">
                <div className="bg-gray-100 p-6 rounded-full">
                  <ShoppingBag size={48} className="text-gray-400" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Your cart is empty</h3>
                  <p className="mt-1 text-sm text-gray-500">Looks like you haven't added anything yet.</p>
                </div>
                <button
                  onClick={() => cart.setIsCartOpen(false)}
                  className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
                >
                  Start Shopping
                </button>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {items.map((item, idx) => {
                  if (!item) return null; // Skip invalid items
                  const imageSrc = item.images && item.images.length > 0 ? item.images[0] : null;

                  return (
                    <li key={`${item.id || idx}-${item.selectedSize}-${item.selectedColor}-${idx}`} className="py-6 flex">
                      <div className="flex-shrink-0 w-24 h-24 border border-gray-200 rounded-md overflow-hidden flex items-center justify-center bg-gray-50">
                        {imageSrc ? (
                             <img
                             src={imageSrc}
                             alt={item.name}
                             className="w-full h-full object-center object-cover"
                             onError={(e) => { (e.target as HTMLImageElement).src = 'https://placehold.co/100x100?text=No+Img'; }}
                           />
                        ) : (
                            <ImageOff size={20} className="text-gray-400" />
                        )}
                      </div>

                      <div className="ml-4 flex-1 flex flex-col">
                        <div>
                          <div className="flex justify-between text-base font-medium text-gray-900">
                            <h3 className="line-clamp-1"><a href="#">{item.name || 'Unknown Item'}</a></h3>
                            <p className="ml-4">Rs. {((item.price || 0) * (item.quantity || 1)).toLocaleString()}</p>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">{item.category} | Size: {item.selectedSize} | Color: {item.selectedColor}</p>
                        </div>
                        <div className="flex-1 flex items-end justify-between text-sm">
                          <div className="flex items-center border border-gray-300 rounded-md">
                            <button
                              onClick={() => cart.updateQuantity(item.id, item.selectedSize, item.selectedColor, Math.max(1, (item.quantity || 1) - 1))}
                              className="p-1 px-2 hover:bg-gray-100 text-gray-600"
                              disabled={(item.quantity || 1) <= 1}
                            >
                              <Minus size={14} />
                            </button>
                            <span className="px-2 font-medium text-gray-900">{item.quantity || 1}</span>
                            <button
                              onClick={() => cart.updateQuantity(item.id, item.selectedSize, item.selectedColor, (item.quantity || 1) + 1)}
                              className="p-1 px-2 hover:bg-gray-100 text-gray-600"
                            >
                              <Plus size={14} />
                            </button>
                          </div>

                          <button
                            type="button"
                            onClick={() => cart.removeFromCart(item.id, item.selectedSize, item.selectedColor)}
                            className="font-medium text-red-600 hover:text-red-500 flex items-center gap-1"
                          >
                            <Trash2 size={16} />
                            <span>Remove</span>
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="border-t border-gray-200 py-6 px-4 sm:px-6 bg-gray-50">
              <div className="flex justify-between text-base font-medium text-gray-900 mb-4">
                <p>Subtotal</p>
                <p>Rs. {(cart.cartTotal || 0).toLocaleString()}</p>
              </div>
              <p className="mt-0.5 text-sm text-gray-500 mb-6">
                Shipping and taxes calculated at checkout.
              </p>
              <div className="space-y-3">
                <button
                  onClick={onCheckout}
                  className="w-full flex justify-center items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  Checkout
                </button>
                <button
                  onClick={() => cart.setIsCartOpen(false)}
                  className="w-full flex justify-center items-center px-6 py-3 border border-gray-300 rounded-md shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors gap-2"
                >
                  <ArrowLeft size={18} />
                  Continue Shopping
                </button>
                <div className="text-center mt-2">
                   <button
                    onClick={cart.clearCart}
                    className="text-sm text-red-500 hover:text-red-700 hover:underline transition-colors"
                  >
                    Clear Cart
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};